import React, {useRef, useState, useCallback} from 'react';
import {
  View,
  Image,
  Modal,
  Pressable,
  Text,
  Dimensions,
  StyleSheet,
  ScrollView,
  Animated,
  PanResponder,
  Platform,
} from 'react-native';
import moment from 'moment';

const {width, height} = Dimensions.get('window');

const ITEM_WIDTH = width * 0.27;
const SIDE_SPACE = (width - ITEM_WIDTH) / 2;
const INACTIVE_SCALE = 0.9;
const INACTIVE_OPACITY = 0.5;

export default function ImageGallery({grAllData, date}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);

  const banglaNumber = {
    0: '০',
    1: '১',
    2: '২',
    3: '৩',
    4: '৪',
    5: '৫',
    6: '৬',
    7: '৭',
    8: '৮',
    9: '৯',
  };
  const engToBdNum = str => {
    for (var x in banglaNumber) {
      str = str.replace(new RegExp(x, 'g'), banglaNumber[x]);
    }
    return str;
  };
  const galleryDate = date || new Date().toLocaleDateString();
  const translateX = useRef(new Animated.Value(0)).current;
  const currentIndex = useRef(0);
  const isAnimating = useRef(false);
  const totalItems = grAllData?.length || 0;

  const getImageUrl = path => 'https://api.bdjudgecourt.com' + path;

  // ── slide to index ──────────────────────────────────────────────────────────
  const slideTo = useCallback(
    (index, animated = true) => {
      const clamped = Math.max(0, Math.min(index, totalItems - 1));
      const targetX = -clamped * ITEM_WIDTH;
      currentIndex.current = clamped;
      setActiveIndex(clamped);

      if (animated) {
        isAnimating.current = true;
        Animated.spring(translateX, {
          toValue: targetX,
          useNativeDriver: false,
          tension: 68,
          friction: 11,
        }).start(() => {
          isAnimating.current = false;
        });
      } else {
        translateX.setValue(targetX);
      }
    },
    [totalItems, translateX],
  );

  const snapToPrev = () => slideTo(currentIndex.current - 1);
  const snapToNext = () => slideTo(currentIndex.current + 1);

  // ── PanResponder — mouse drag + touch swipe ─────────────────────────────────
  const startVal = useRef(0);
  const isDragging = useRef(false);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onStartShouldSetPanResponderCapture: () => false,
      onMoveShouldSetPanResponder: (_, gs) =>
        Math.abs(gs.dx) > Math.abs(gs.dy) && Math.abs(gs.dx) > 4,
      onMoveShouldSetPanResponderCapture: (_, gs) =>
        Math.abs(gs.dx) > Math.abs(gs.dy) && Math.abs(gs.dx) > 4,

      onPanResponderGrant: () => {
        isDragging.current = false;
        translateX.stopAnimation(val => {
          startVal.current = val;
        });
      },

      onPanResponderMove: (_, gs) => {
        if (Math.abs(gs.dx) > 4) {
          isDragging.current = true;
          const raw = startVal.current + gs.dx;
          const min = -(totalItems - 1) * ITEM_WIDTH;
          const max = 0;
          let val = raw;
          if (raw < min) val = min - (min - raw) * 0.2;
          if (raw > max) val = max + (raw - max) * 0.2;
          translateX.setValue(val);
        }
      },

      onPanResponderRelease: (_, gs) => {
        const threshold = ITEM_WIDTH * 0.18;
        if (gs.dx < -threshold || gs.vx < -0.5) {
          slideTo(currentIndex.current + 1);
        } else if (gs.dx > threshold || gs.vx > 0.5) {
          slideTo(currentIndex.current - 1);
        } else {
          slideTo(currentIndex.current);
        }
      },

      onPanResponderTerminate: () => slideTo(currentIndex.current),
    }),
  ).current;

  // ── double-tap on CAROUSEL card ─────────────────────────────────────────────
  const lastCarouselTap = useRef(null);
  const handleCarouselDoubleTap = index => {
    const now = Date.now();
    if (lastCarouselTap.current && now - lastCarouselTap.current < 300) {
      lastCarouselTap.current = null;
      setActiveIndex(index);
      setZoomScale(1);
      setVisible(true);
    } else {
      lastCarouselTap.current = now;
    }
  };

  // ── double-tap on THUMBNAIL → open modal ────────────────────────────────────
  const lastThumbTap = useRef({});
  const handleThumbDoubleTap = index => {
    const now = Date.now();
    const prev = lastThumbTap.current[index] || 0;
    if (now - prev < 300) {
      lastThumbTap.current[index] = 0;
      // single tap already selected it, now open modal
      setActiveIndex(index);
      setZoomScale(1);
      setVisible(true);
    } else {
      lastThumbTap.current[index] = now;
      slideTo(index); // first tap = select
    }
  };

  // ── zoom ────────────────────────────────────────────────────────────────────
  const openImage = index => {
    setActiveIndex(index);
    setZoomScale(1);
    setVisible(true);
  };
  const zoomIn = () => setZoomScale(p => Math.min(p + 0.5, 5));
  const zoomOut = () => setZoomScale(p => Math.max(p - 0.5, 1));

  if (!grAllData?.length) {
    return (
      <View style={styles.empty}>
        <Text>No data found</Text>
      </View>
    );
  }

  return (
    // ── outer ScrollView — vertical scroll এনাবল ──────────────────────────────
    <ScrollView
      style={styles.outerScroll}
      contentContainerStyle={styles.outerScrollContent}
      showsVerticalScrollIndicator={false}
      scrollEnabled={true}
      nestedScrollEnabled={true}
    >
      {console.log(grAllData) /* ── THUMBNAILS ── */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled={true}
        style={{marginBottom: 12}}
      >
        {grAllData.map((item, index) => (
          <Pressable
            key={index}
            onPress={() => handleThumbDoubleTap(index)}
            style={[styles.thumb, activeIndex === index && styles.activeThumb]}
          >
            <Image
              source={{uri: getImageUrl(item.file_path)}}
              style={[
                styles.thumbImg,
                activeIndex === index && styles.thumbImgActive,
              ]}
            />
            {console.log(item.date) /* double-tap hint badge on active thumb */}
            {activeIndex === index && (
              <View style={styles.thumbBadge}>
                <Text style={styles.thumbBadgeText}>
                  {galleryDate
                    ? engToBdNum(moment(galleryDate).format('DD-MM-YYYY'))
                    : engToBdNum(
                        moment(new Date(galleryDate)).format('DD-MM-YYYY'),
                      )}
                </Text>
              </View>
            )}
          </Pressable>
        ))}
      </ScrollView>

      {/* ── CONTROLS ── */}
      {/* <View style={styles.controls}>
        <Pressable onPress={snapToPrev} style={styles.btn}>
          <Text style={styles.btnText}>❮</Text>
        </Pressable>

        <Pressable
          onPress={() => openImage(activeIndex)}
          style={styles.zoomBtn}
        >
          <Text style={styles.zoomBtnText}>🔍 Zoom</Text>
        </Pressable>

        <Pressable onPress={snapToNext} style={styles.btn}>
          <Text style={styles.btnText}>❯</Text>
        </Pressable>
      </View> */}

      {/* ── CAROUSEL ── */}
      <View style={styles.carouselWrapper} {...panResponder.panHandlers}>
        <Animated.View
          style={[
            styles.carouselTrack,
            {transform: [{translateX}], width: ITEM_WIDTH * totalItems},
          ]}
        >
          {grAllData.map((item, index) => {
            const inputRange = [
              -(index + 1) * ITEM_WIDTH,
              -index * ITEM_WIDTH,
              -(index - 1) * ITEM_WIDTH,
            ];

            const scaleAnim = translateX.interpolate({
              inputRange,
              outputRange: [INACTIVE_SCALE, 1, INACTIVE_SCALE],
              extrapolate: 'clamp',
            });
            const opacityAnim = translateX.interpolate({
              inputRange,
              outputRange: [INACTIVE_OPACITY, 1, INACTIVE_OPACITY],
              extrapolate: 'clamp',
            });
            const tyAnim = translateX.interpolate({
              inputRange,
              outputRange: [18, 0, 18],
              extrapolate: 'clamp',
            });

            return (
              <Animated.View
                key={index}
                style={[
                  styles.card,
                  {
                    width: ITEM_WIDTH,
                    opacity: opacityAnim,
                    transform: [
                      {scaleX: scaleAnim},
                      {scaleY: scaleAnim},
                      {translateY: tyAnim},
                    ],
                  },
                ]}
              >
                <Pressable
                  onPress={() => handleCarouselDoubleTap(index)}
                  style={StyleSheet.absoluteFill}
                />

                <Image
                  source={{uri: getImageUrl(item.file_path)}}
                  style={styles.image}
                  resizeMode="cover"
                  pointerEvents="none"
                />
                <View style={styles.imageBadge}>
                  <Text style={styles.imageBadgeText}>
                    {'তারিখঃ '}
                    {galleryDate
                      ? engToBdNum(moment(galleryDate).format('DD-MM-YYYY'))
                      : engToBdNum(
                          moment(new Date(galleryDate)).format('DD-MM-YYYY'),
                        )}
                  </Text>
                </View>
              </Animated.View>
            );
          })}
        </Animated.View>
      </View>

      {/* ── DOTS ── */}
      <View style={styles.dots}>
        {grAllData.map((_, i) => {
          const inputRange = [
            -(i + 1) * ITEM_WIDTH,
            -i * ITEM_WIDTH,
            -(i - 1) * ITEM_WIDTH,
          ];
          const dotWidth = translateX.interpolate({
            inputRange,
            outputRange: [8, 24, 8],
            extrapolate: 'clamp',
          });
          const dotBg = translateX.interpolate({
            inputRange,
            outputRange: ['#ccc', '#2563eb', '#ccc'],
            extrapolate: 'clamp',
          });
          return (
            <Animated.View
              key={i}
              style={[styles.dot, {width: dotWidth, backgroundColor: dotBg}]}
            />
          );
        })}
      </View>

      {/* ── MODAL ── */}
      <Modal visible={visible} transparent animationType="fade">
        <View style={styles.modal}>
          <ScrollView
            style={styles.modalScrollOuter}
            contentContainerStyle={styles.zoomWrap}
            maximumZoomScale={5}
            minimumZoomScale={1}
            centerContent={true}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            bounces={true}
            bouncesZoom={true}
            scrollEnabled={true}
            pinchGestureEnabled={true} // জরুরি
            scrollEventThrottle={16}
          >
            <Image
              source={{uri: getImageUrl(grAllData[activeIndex]?.file_path)}}
              style={[
                styles.fullImage,
                {
                  width: width * zoomScale,
                  height: height * 0.85 * zoomScale,
                },
              ]}
              resizeMode="contain"
            />
          </ScrollView>

          {/* Zoom Controls */}
          <View style={styles.zoomOverlay}>
            <Pressable onPress={zoomOut} style={styles.zbtn}>
              <Text style={styles.ztext}>−</Text>
            </Pressable>
            <Text style={styles.zoomLabel}>{Math.round(zoomScale * 100)}%</Text>
            <Pressable onPress={zoomIn} style={styles.zbtn}>
              <Text style={styles.ztext}>+</Text>
            </Pressable>
          </View>

          {/* Close Button */}
          <Pressable onPress={() => setVisible(false)} style={styles.closeBtn}>
            <Text style={styles.ztext}>✕</Text>
          </Pressable>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  imageBadge: {
    position: 'absolute',
    // top: 12,
    top: '12%', // সেন্টারে
    left: '33%',
    transform: [{translateX: -50}, {translateY: -50}],
    backgroundColor: 'rgba(0, 0, 0, 0.55)', // সেমি-ট্রান্সপারেন্ট কালো
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    zIndex: 2,
  },
  imageBadgeText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  // outer scroll container
  outerScroll: {
    width: '100%',
  },
  outerScrollContent: {
    alignItems: 'center',
    paddingVertical: 12,
  },

  carouselWrapper: {
    width: width,
    overflow: 'hidden',
    paddingLeft: SIDE_SPACE,
    ...Platform.select({web: {cursor: 'grab'}}),
  },

  carouselTrack: {
    flexDirection: 'row',
  },

  card: {
    borderRadius: 18,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 10},
        shadowOpacity: 0.22,
        shadowRadius: 18,
      },
      android: {elevation: 10},
      web: {boxShadow: '0 10px 36px rgba(0,0,0,0.22)'},
    }),
  },

  image: {
    width: '100%',
    height: 420,
    borderRadius: 18,
  },

  // thumbnails
  thumb: {
    marginHorizontal: 5,
    borderRadius: 10,
    borderWidth: 2.5,
    borderColor: 'transparent',
  },

  activeThumb: {
    borderColor: '#2563eb',
    ...Platform.select({
      web: {boxShadow: '0 0 0 3px #2563eb55'},
    }),
  },

  thumbImg: {
    width: 62,
    height: 62,
    borderRadius: 8,
  },

  thumbImgActive: {
    width: 76,
    height: 76,
    borderRadius: 9,
  },

  thumbBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.65)', // সেমি-ট্রান্সপারেন্ট কালো
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 1,
    alignSelf: 'center',
  },

  thumbBadgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: 'bold',
  },

  controls: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 10,
  },

  btn: {
    backgroundColor: '#2563eb',
    padding: 5,
    marginHorizontal: 5,
    borderRadius: 25,
  },

  btnText: {color: '#fff', fontSize: 10},

  zoomBtn: {
    backgroundColor: '#111827',
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 10,
  },

  zoomBtnText: {color: '#fff', fontSize: 10},

  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 14,
  },

  dot: {
    height: 8,
    borderRadius: 10,
    marginHorizontal: 4,
  },

  modal: {
    flex: 1,
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.95)',
  },
  modalScrollOuter: {
    flex: 1,
  },
  zoomWrap: {
    flexGrow: 1, // এটা গুরুত্বপূর্ণ
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20, // অতিরিক্ত স্পেস দিলে ভালো হয়
  },

  fullImage: {
    width: width,
    height: height * 0.85,
  },

  zoomOverlay: {
    position: 'absolute',
    bottom: 50,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    padding: 10,
    borderRadius: 30,
    gap: 15,
  },
  zbtn: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ztext: {
    color: 'white',
    fontSize: 28,
    fontWeight: 'bold',
  },

  zoomLabel: {
    color: 'white',
    fontSize: 18,
    minWidth: 60,
    textAlign: 'center',
  },
  closeBtn: {
    position: 'absolute',
    top: 50,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  empty: {
    padding: 20,
    alignItems: 'center',
  },
});
